import { useEffect, useState } from "react";
import useAxiosClient from "../axios-client";
import { COMPANY_FALLBACK, readStoredCompany } from "../utils/company";

/**
 * The company's branding for screens that run before anyone is signed in.
 *
 * Reads GET /company/public, which answers without a token and returns only
 * name, tagline and logo — the login screen has no business knowing the
 * company's phone number, email or tax identifiers.
 *
 * Starts from the profile the shell cached on a previous visit so a returning
 * user's brand paints immediately, and falls back to the literals otherwise.
 * Deliberately never writes to that cache: the public payload is three fields,
 * and storing it would leave an invoice printing a letterhead with a name and
 * no address.
 */
export const usePublicCompany = () => {
  const axiosClient = useAxiosClient();
  const [company, setCompany] = useState(() => readStoredCompany() || COMPANY_FALLBACK);

  useEffect(() => {
    let cancelled = false;

    axiosClient
      .get("/company/public")
      .then(({ data }) => {
        // Merge rather than replace: whatever the cache held stays, and only
        // the three public fields are refreshed.
        if (!cancelled && data?.data?.name) {
          setCompany((current) => ({ ...current, ...data.data }));
        }
      })
      .catch(() => { /* the cached brand, or the fallback, still renders */ });

    return () => { cancelled = true; };
  }, [axiosClient]);

  return company;
};
